const iframe = ({ title, left, leftSrc, rightSrc }) => {
  return `
  <iframe width="100%" height="216" src="${left ? leftSrc : rightSrc}" title="${
    left ? 'Left-handed //' : ''
  } ${title}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
  `;
};

const gettingStarted = ({ left }) => `
<br/>
<p>You might not know yet what all these materials are for, but fret not, it'll all be revealed soon. The only other things you&apos;ll need are scissors, and you&apos;re good to go!</p>
           
<h3>Pre-started crochet piece</h3>
<p>To get you right into the thick of things, your kit came pre-started for you. Because this pre-started piece is one of the trickiest techniques in crochet, keep it together until you're more confident in your crochet skills. Watch the video to set things up so that you can't unravel your pre-started piece.</p>
<iframe width="100%" height="216" src="https://www.youtube.com/embed/jzV_NOT7rRU" title="How to prevent the pre-started piece from coming apart" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>

<h3>What you need to know to wooble</h3>
${iframe({
  title: 'How to wooble',
  left,
  leftSrc: 'https://www.youtube.com/embed/bCFqzswiQWg',
  rightSrc: 'https://www.youtube.com/embed/x53yS1AGp0U',
})}
`;

const basics = ({ left }) => ({
  display_name: 'The basics',
  steps: [
    {
      display_name: 'How to hold a hook & yarn',
      content: `
      ${iframe({
        title: 'Hold a hook &amp; yarn',
        left,
        leftSrc: 'https://www.youtube.com/embed/FeQi3cgwNWo',
        rightSrc: 'https://www.youtube.com/embed/HOS4X0bjySs',
      })}
      `,
    },
    {
      display_name: 'Single crochet stitch',
      content: `
      <p>Learn this one stitch, and you'll be able to make most of your Wooble:</p>
      ${iframe({
        title: 'How to single crochet stitch (sc)',
        left,
        leftSrc: 'https://www.youtube.com/embed/nyixlcOqZWk',
        rightSrc: 'https://www.youtube.com/embed/5TNGOablBCU',
      })}
      `,
    },
    {
      display_name: 'Increase stitch',
      content: `
      <p>While this may sound like another stitch, it's actually the same technique as the single crochet stitch – you'll just do it twice in the same exact spot. This is the stitch you'll use to make Round 2:</p>
      ${iframe({
        title: 'Increase stitch',
        left,
        leftSrc: 'https://www.youtube.com/embed/UkiURC3OYx8',
        rightSrc: 'https://www.youtube.com/embed/ePL3Ml12XCo',
      })}
      `,
    },
    {
      display_name: 'Round 2',
      content: `
    <p>For round 2, crochet 6 increase stitches. Crocheters love abbreviations, so in pattern speak, that's written as:</p>
    <p class="ctr"><b>Round 2:</b> inc x 6 (12)</p>
    ${iframe({
      title: 'Round 2',
      left,
      leftSrc: 'https://www.youtube.com/embed/yu4L8riJm-k',
      rightSrc: 'https://www.youtube.com/embed/N3ea0j06180',
    })}
    <h3>How does 6 = 12?</h3>
    ${iframe({
      title: 'How 6 inc = 12 stitches',
      left,
      leftSrc: 'https://www.youtube.com/embed/hoUqKAIkqLM',
      rightSrc: 'https://www.youtube.com/embed/y10NizzqSQQ',
    })}
    <p>For round 2, you crocheted 6 increase stitches.</p>
    <p class="ctr">inc x 6</p>
    <p>Since 1 increase stitch = 2 single crochet stitches in the same stitch, 1 increase stitch counts as 2 stitches. Which means the pattern could've also been written like this:</p>
    <p class="ctr">[2 single crochet stitches in the same stitch] x 6</p>
    <p>And</p>
    <p class="ctr">2 stitches x 6 = 12 total stitches</p>
    <p>Hooray for math! 🤓</p>
    `,
    },
  ],
});

const body = ({ left }) => [
  {
    display_name: 'Round 3 & reading patterns',
    content: `
  <p>To make Round 3, we're going to use both of the stitches you've already learned – the single crochet and increase stitch. Here's what Round 3 looks like written out:</p>
  <p class="ctr">Round 3: [sc, inc] x 6   (18)</p>
  <p>Don't worry, it's not as scary as it looks. You actually already know all of the things you need to know to make Round 3. If you want to see where we're going with Round 3, watch this video:</p>
  ${iframe({
    title: 'Understanding Round 3',
    left,
    leftSrc: 'https://www.youtube.com/embed/Ee1mTr1R0EU',
    rightSrc: 'https://www.youtube.com/embed/Jxvi2cI0Hgg',
  })}
  <p>Then follow this video to make sure you're crocheting Round 3 the right way:</p>
  ${iframe({
    title: 'Round 3',
    left,
    leftSrc: 'https://www.youtube.com/embed/3uaxS2L_r9Y',
    rightSrc: 'https://www.youtube.com/embed/44d3PxSFfN8',
  })}
  <h3>How to read a pattern</h3>
  <p>For round 3, you repeated the sequence of [1 sc followed by 1 inc] a total of 6 times. In pattern lingo, that's written as:</p>
  <p class="ctr">[sc, inc] x 6</p>
  <p>Which also could've been written as:</p>
  <p class="ctr">[sc, inc]    [sc, inc]    [sc, inc]    [sc, inc]    [sc, inc]    [sc, inc]</p>
  <p>Or with a lot more words:</p>
  <p class="ctr">[single crochet stitch in the first stitch, then 2 single crochet stitches in the next stitch] for a total of 6 times</p>
  <p>(Are you starting to dig abbreviations yet? 😉)</p>
  <p>Since 1 increase stitch = 2 single crochet stitches, 1 increase stitch counts as 2 stitches. 1 single crochet stitch counts as 1 stitch.</p>
  <p>Therefore, that's [3 stitches] repeated 6 times, for a final count of 18 stitches.</p>
  <img style="width: 100%" src="https://cdn.shopify.com/s/files/1/0400/0904/0038/files/pattern_explanation_480x480.jpg?v=1602288492"/>
  <h3>Why all this fuss about counting?</h3>
  <p>If you come out with a plushie or a potholder, all depends on the number and type of stitches you do each round. So keep count while you crochet, or count your stitches every time you finish a round.</p>
  `,
  },
];

const eyes = {
  display_name: 'Adding eyes & stuffing',
  content: `
<p>Wooble-yeah – you're so close! There's only one round left for the body. Before you close it up, add the eyes and stuffing.</p>
<p>⚠️ If you're going to give this to a child younger than 3 or to a pet, don't use safety eyes! Safety eyes aren&apos;t safe. Confusing, I know. You can embroider eyes on instead.</p>
<p>If safety eyes are a go, then put the bigger eyes between rounds 4 & 5, with 4 stitches in between them. The flat side of the backing should face the yarn. Watch the video below for a demo on how to count rounds and stitches, and put safety eyes in correctly:</p>
<iframe width="100%" height="216" src="https://www.youtube.com/embed/Oz1LBEtgy34" title="How to put safety eyes on crochet amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
<p>Once you add the eyes, stuff your amigurumi and shape it like an egg.</p>
<iframe width="100%" height="216" src="https://www.youtube.com/embed/JEwyvrIs7Zc" title="How to stuff amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>  
`,
};

const goodBye = src => `
<h3>Hooray, you dun it!</h3>
<img style="width: 100%" src="${src}"/>
<p>And there ya have it, your very own woobly three peas in a pod!</p>
<p>Every Wooble comes out a little bit different, and that's part of their charm. Tag us on Instagram @thewoobles, so we can ooooh and aaaaah at your woobly wonder.</p>
<p>Now that you know how to crochet, you can make these Three Peas in a Pod again and again by following the pattern.</p>
<p>Until we meet again, wooble on!</p>
`;

const arms = {
  display_name: 'The arms',
  content: `
  <div>
    <p>Lastly, most amigurumi have fixed arms, but one of the signature traits of a&nbsp;Wooble are their wide-swinging arms! Here's a video on how to attach movable arms to any amigurumi:</p>
    <iframe width="100%" height="216" src="https://www.youtube.com/embed/s6Tkj1Ej8bI" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
  </div>
  `,
};

const other = {
  display_name: 'Other tutorials',
  content: `
  <p><span>For more stitches and techniques, check out all of our&nbsp;<a href="https://thewoobles.com/pages/tutorials">tutorials</a>.&nbsp;</span></p>
  <p>Got stuck? S<span>end&nbsp;us an email at&nbsp;</span><a href="mailto:help@thewoobles.com" aria-describedby="a11y-external-message">help@thewoobles.com</a><span>.</span></p>
  `,
};

const done = {
  display_name: 'Done!',
  steps: [
    {
      display_name: 'Share your progress',
      content: `
      <div>
        <p>We love seeing the Woobles that people create. Tag us on Instagram <a href="https://www.instagram.com/thewoobles" target="_blank" rel="noopener noreferrer" aria-describedby="a11y-new-window-external-message">@thewoobles</a>, so we can cheer you on!</p>
        <p>&nbsp;</p>
        <div style="text-align: center;"><a class="btn" href="https://thewoobles.com/crochet-kits">SEE MORE KITS</a></div>
      </div>
      `,
    },
  ],
};

const harrietSections = {
  sections: [
    {
      display_name: 'Getting started',
      steps: [
        {
          display_name: `
          What's in your kit`,
          content: `
      <ul>
      <li>2mm hook (if you paid the additional charge)</li>
      <li>Tapestry needle</li>
      <li>Stuffing</li>
      <li>Stitch markers</li>
      <li>8mm safety eyes</li>
      <li>Gray fingering-weight cotton yarn</li>
      <li>Pink fingering-weight cotton yarn</li>
      </ul>
      <br/>
      <p>You'll also need scissors to cut your yarn, and you may want to use pins to plan where exactly you want to attach the various amigurumi pieces.</p>
      `,
        },
      ],
    },
    {
      display_name: 'Tutorials for the tricky parts',
      steps: [
        {
          display_name: 'The legs',
          content: `
          <div>
            <p><span>Crocheting&nbsp;Harriet's legs&nbsp;can get a little tricky, so watch these&nbsp;videos to see how to make&nbsp;each of&nbsp;her legs and sew the gap in between.</span></p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/UM2J0qSWwQ4" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/l2cddEKKCJk" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/8iXsvEumNVU" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
          </div>
          `,
        },
        arms,
        other,
      ],
    },
    done,
  ],
};

const hippoSections = {
  sections: [
    {
      display_name: 'Getting started',
      steps: [
        {
          display_name: `
          What's in your kit`,
          content: `
          <ul>
            <li>2mm hook (if you paid the additional charge)</li>
            <li>Poly-fil stuffing</li>
            <li>8mm safety eyes</li>
            <li>Stitch marker</li>
            <li>Tapestry needle</li>
            <li>Purple sport-weight cotton yarn</li>
            <li>Pink sport-weight cotton yarn</li>
          </ul>
          <br/>
          <p>You'll also need scissors to cut your yarn, and you may want to use pins to plan where exactly you want to attach the various amigurumi pieces.</p>
      `,
        },
      ],
    },
    {
      display_name: 'Tutorials for the tricky parts',
      steps: [
        {
          display_name: 'The legs',
          content: `
          <div>
            <p><span>Crocheting Poppi's legs can get a little tricky, so watch these videos to see how to make each of her legs and sew the gap in between. All Woobles intermediate patterns use the same technique for the legs, which is why the video shows a bear instead of a hippo:</span></p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/UM2J0qSWwQ4" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/l2cddEKKCJk" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/8iXsvEumNVU" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
          </div>
          `,
        },
        arms,
        {
          display_name: 'The ears and snout',
          content: `
          <p>Poppi's ears involve alternating between two colors. Watch this tutorial to learn how to change colors throughout a round.</p>
          <iframe width="100%" height="216" src="https://www.youtube.com/embed/YyC-VdgiZy4" title="How to change colors in crochet" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
          <p>Poppi's snout is made by creating an oval. For folks who're new to this technique, here's a video:</p>
          <iframe width="100%" height="216" src="https://www.youtube.com/embed/mNXTJf0XR6c" title="Start a crochet oval with 4 foundation chain stitches" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
          <p>And there comes a point where you'll need to do a new stitch, abbreviated "dc5tog". This is shorthand for "double crochet 5 stitches together", also known as a bobble stitch:</p>
          <iframe width="100%" height="216" src="https://www.youtube.com/embed/86G8f3MENVg" title="How to bobble stitch (dc5tog) in crochet" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
          `,
        },
        other,
      ],
    },
    done,
  ],
};

const foxSections = {
  sections: [
    {
      display_name: 'Getting started',
      steps: [
        {
          display_name: `
          What's in your kit`,
          content: `
      <ul>
        <li>2mm hook (if you paid the additional charge)</li>
        <li>Poly-fil stuffing</li>
        <li>8mm safety eyes</li>
        <li>Stitch marker</li>
        <li>Tapestry needle</li>
        <li>Orange fingering-weight cotton yarn</li>
        <li>White fingering-weight cotton yarn</li>
        <li>Black fingering-weight cotton yarn</li>
        <li>Green fingering-weight cotton yarn</li>
        <li>Light green fingering-weight cotton yarn</li>
        <li>Yellow fingering-weight cotton yarn</li>
      </ul>
      <br/>
      <p>You'll also need scissors to cut your yarn, and you may want to use pins to plan where exactly you want to attach the various amigurumi pieces.</p>
      `,
        },
      ],
    },
    {
      display_name: 'Tutorials for the tricky parts',
      steps: [
        {
          display_name: 'The legs',
          content: `
          <div>
            <p>Crocheting Francois's legs can get a little tricky, so watch these videos to see how to make each of his legs and sew the gap in between. All Woobles intermediate patterns use the same technique for the legs, which is why the video shows a bear instead of a fox:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/UM2J0qSWwQ4" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/l2cddEKKCJk" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/8iXsvEumNVU" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
          </div>
          `,
        },
        arms,
        other,
      ],
    },
    done,
  ],
};

const klausSections = {
  sections: [
    {
      display_name: 'Getting started',
      steps: [
        {
          display_name: `
          What's in your kit`,
          content: `
      <ul>
        <li>2mm hook (if you paid the additional charge)</li>
        <li>Tapestry needle</li>
        <li>Stitch markers</li>
        <li>8mm safety eyes</li>
        <li>Poly-fil stuffing</li>
        <li>Light green fingering-weight cotton yarn</li>
        <li>Dark green fingering-weight cotton yarn</li>
      </ul>
      <br/>
      <p>You'll also need scissors to cut your yarn, and you may want to use pins to plan where exactly you want to attach the various amigurumi pieces.</p>
      `,
        },
      ],
    },
    {
      display_name: 'Tutorials for the tricky parts',
      steps: [
        {
          display_name: 'The snout',
          content: `
          <p>Klaus's pattern starts off with the snout, where we make an oval starting with 6 foundation chain stitches. Starting an oval for the first time can be a bit confusing, so here's a tutorial to explain:</p>
          <iframe width="100%" height="216" src="https://www.youtube.com/embed/3jyeF3e6RO0" title="Start a crochet oval with 6 foundation chain stitches" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
          <p>And there comes a point where you'll need to do a new stitch, abbreviated "dc5tog". This is shorthand for "double crochet 5 stitches together", also known as a bobble stitch:</p>
          <iframe width="100%" height="216" src="https://www.youtube.com/embed/86G8f3MENVg" title="How to bobble stitch (dc5tog) in crochet" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
          `,
        },
        {
          display_name: 'The legs',
          content: `
          <div>
            <p>Crocheting Klaus's legs can get a little tricky, so watch these videos to see how to make each of his legs and sew the gap in between. All Woobles intermediate patterns use the same technique for the legs, which is why the video shows a bear instead of a crocodile:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/UM2J0qSWwQ4" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/l2cddEKKCJk" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/8iXsvEumNVU" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
          </div>
          `,
        },
        arms,
        other,
      ],
    },
    done,
  ],
};

const bearSections = {
  sections: [
    {
      display_name: 'Getting started',
      steps: [
        {
          display_name: `
          What's in your kit`,
          content: `
      <ul>
        <li>2mm hook (if you paid the additional charge)</li>
        <li>Tapestry needle</li>
        <li>Stitch markers</li>
        <li>8mm safety eyes</li>
        <li>Poly-fil stuffing</li>
        <li>Light green fingering-weight cotton yarn</li>
        <li>Dark green fingering-weight cotton yarn</li>
      </ul>
      <br/>
      <p>You'll also need scissors to cut your yarn, and you may want to use pins to plan where exactly you want to attach the various amigurumi pieces.</p>
      `,
        },
      ],
    },
    {
      display_name: 'Tutorials for the tricky parts',
      steps: [
        {
          display_name: 'The legs',
          content: `
          <div>
            <p>Crocheting Fitzherbert's legs can get a little tricky, so watch these videos to see how to make each of his legs and sew the gap in between.</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/UM2J0qSWwQ4" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/l2cddEKKCJk" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/8iXsvEumNVU" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="" class="ui-droppable"></iframe>
          </div>
          `,
        },
        {
          display_name: 'The snout',
          content: `
          <p>Fitzherbert's snout is made by creating an oval. For folks who're new to this technique, here's a video:</p>
          <iframe width="100%" height="216" src="https://www.youtube.com/embed/3jyeF3e6RO0" title="Start a crochet oval with 6 foundation chain stitches" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
          `,
        },
        arms,
        other,
      ],
    },
    done,
  ],
};

const generalSections = left => ({
  sections: [
    {
      display_name: 'General tutorials',
      content: `
      <p>
        Looking for videos on general stitches and techniques?
        Check out our <a href="https://thewoobles.com/pages/${
          left ? 'left-handed-crochet-tutorials' : 'crochet-tutorials'
        }">
          ${left ? 'left' : 'right'}-handed video tutorials
        </a>.
      </p>
      <p>Looking for help with some of the trickier parts of specific patterns in <i>Crochet Amigurumi for Every Occasion</i>? Check out the videos below.</p>
      `,
      steps: [],
    },
    // {
    //   display_name: 'General tutorials',
    //   steps: [
    //     {
    //       display_name: 'Crochet basics',
    //       content: [
    //         {
    //           title: 'Hold a hook & yarn',
    //           desc: 'First things first, learn how to hold a crochet hook in one hand and yarn in the other.',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-hold-crochet-hook-and-yarn_02767ac4-bddf-4a51-aa76-49d645d142aa_1296x.webp?v=1661382646',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-hold-a-crochet-hook-and-yarn-for-lefties'
    //             : 'https://thewoobles.com/pages/hold-crochet-hook-and-yarn',
    //         },
    //         {
    //           title: 'Count stitches, rounds, and rows',
    //           desc: 'How to count stitches, rounds, and rows.',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-count-stitches-crochet_ea24c4f5-951c-4f30-8189-dba346c471f1_1296x.jpg?v=1661452747',
    //           url: left
    //             ? 'https://thewoobles.com/pages/parts-of-a-crochet-stitch-for-lefties'
    //             : 'https://thewoobles.com/pages/parts-of-a-crochet-stitch',
    //         },
    //         {
    //           title: 'Identify the right side of crochet',
    //           desc: 'Once you&apos;ve crocheted a few rounds, you work will naturally curve. But which side should actually face out?',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-right-side-vs-wrong-side-crochet_1296x.webp?v=1661380543',
    //           url: left
    //             ? 'https://thewoobles.com/pages/right-and-wrong-side-of-crochet-for-lefties'
    //             : 'https://thewoobles.com/pages/right-versus-wrong-sides',
    //         },
    //         {
    //           title: 'Use stitch markers',
    //           desc: 'Learn how to use stitch markers to keep track of where you are.',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-use-stitch-markers-crochet_1296x.webp?v=1661380649',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-use-stitch-markers-left-handed-crochet'
    //             : 'https://thewoobles.com/pages/how-to-use-stitch-marker-crochet',
    //         },
    //         {
    //           title: 'Crochet abbreviations',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/crochet-abbreviations_1296x.webp?v=1661381000',
    //           desc: 'Quick reference guide for US and UK abbreviations and terminology.',
    //           url: left
    //             ? 'https://thewoobles.com/pages/crochet-abbreviations-for-lefties'
    //             : 'https://thewoobles.com/pages/crochet-tutorials',
    //         },
    //         {
    //           title: 'Read a crochet pattern',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/crochet-pattern_1296x.webp?v=1661381098',
    //           desc: 'Learn how to understand crochet pattern instructions.',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-read-a-crochet-pattern-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-read-a-crochet-pattern',
    //         },
    //         {
    //           title: 'Yarn over',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-yarn-over-in-crochet_1296x.webp?v=1661381168',
    //           desc: 'Learn what it means to yarn over – the most basic building block of almost every stitch.',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-yarn-over-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-yarn-over-in-crochet',
    //         },
    //       ],
    //     },
    //     {
    //       display_name: 'Crochet stitches',
    //       content: [
    //         {
    //           title: 'Slip stitch (sl st)',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-slip-stitch-crochet-how-to_1296x.webp?v=1661381319',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-slip-stitch-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-slip-stitch',
    //           desc: 'e of the quickest stitches in crochet, often used as part of the technique to fasten off a round crochet piece.',
    //         },
    //         {
    //           title: 'Single crochet (sc)',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-single-crochet-stitch_dc1d5911-bc62-4f94-8136-c09b3342d0f9_1296x.jpg?v=1661452809',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-single-crochet-stitch-for-lefties'
    //             : 'https://thewoobles.com/pages/single-crochet-stitch',
    //           desc: 'The basic stitch for crochet, including amigurumi.',
    //         },
    //         {
    //           title: 'Increase (inc)',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-increase-stitch-in-crochet_1296x.webp?v=1661381511',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-single-crochet-increase-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-single-crochet-increase',
    //           desc: 'Single crochet twice into the same stitch, to make your piece grow larger. Learn both the regular increase and invisible increase.',
    //         },
    //         {
    //           title: 'Decrease (dec)',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-decrease-crochet_1296x.webp?v=1661381656',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-single-crochet-decrease-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-decrease-single-crochet',
    //           desc: 'Reduce the size of your piece by turning two stitches into one. Learn both the regular decrease and invisible decrease.',
    //         },
    //         {
    //           title: 'Chain (ch)',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-chain-stitch-in-crochet_5f6eb794-fdcc-40be-baca-d59df8323c9e_1296x.webp?v=1661381688',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-crochet-a-chain-stitch-ch-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-crochet-a-chain-stitch-ch',
    //           desc: 'Another quick crochet stitch, often used to create loops like basket handles, or embellishments like doll hair.',
    //         },
    //         {
    //           title: 'Turning chain (ch 1 & turn)',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-turning-chain-in-crochet_1296x.webp?v=1661381768',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-crochet-a-turning-chain-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-turning-chain',
    //           desc: 'Use this technique to crochet new rows.',
    //         },
    //         {
    //           title: 'Half double crochet (hdc)',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-half-double-crochet-how-to_1296x.webp?v=1661381818',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-half-double-crochet-stitch-hdc-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-half-double-crochet-stitch-hdc',
    //           desc: 'This stitch is the halfway point between a single and double crochet stitch.',
    //         },
    //         {
    //           title: 'Double crochet (dc)',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-double-crochet-stitch_1296x.jpg?v=1661212944',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-double-crochet-stitch-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-double-crochet-stitch-dc',
    //           desc: 'The double crochet stitch is twice the height of a single crochet stitch.',
    //         },
    //         {
    //           title: 'Bobble stitch (dc5tog)',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-bobble-stitch-dc5tog-in-crochet_1296x.webp?v=1661381881',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-bobble-stitch-for-lefties-dc5tog'
    //             : 'https://thewoobles.com/pages/how-to-bobble-stitch-dc5tog',
    //           desc: 'Crochet 5 double crochet stitches together to create a bobble stitch.',
    //         },
    //         {
    //           title: 'Single crochet 3 together (sc3tog)',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-single-crochet-3-together-sc3tog_1296x.webp?v=1661381916',
    //           url: left
    //             ? 'https://thewoobles.com/pages/sc3tog-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-single-crochet-3-stitches-together-sc3tog',
    //           desc: 'Take three stitches and turn them into one',
    //         },
    //       ],
    //     },
    //     {
    //       display_name: 'Techniques',
    //       content: [
    //         {
    //           title: 'Undo stitches',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-undo-crochet-stitches_1296x.webp?v=1661382003',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-undo-stitches-in-the-round-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-undo-stitches-in-the-round',
    //           desc: 'Made a mistake? Don&apos;t sweat it. Learn how to undo your mistakes and get back on track.',
    //         },
    //         {
    //           title: 'Magic ring',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-magic-ring-in-crochet_1296x.webp?v=1661382029',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-make-a-magic-loop-for-lefties'
    //             : 'https://thewoobles.com/pages/crochet-magic-loop',
    //           desc: 'Also known as a magic loop, magic circle, adjustable ring, or drawstring ring. This is a popular way to start round crochet pieces.',
    //         },
    //         {
    //           title: 'Start round piece with a chain',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-start-a-round-crochet-piece-with-a-chain_112a843c-8f2a-4b16-bb49-f5972b294e78_1296x.webp?v=1661536561',
    //           url: left
    //             ? 'https://thewoobles.com/pages/start-round-piece-with-chain-for-lefties'
    //             : 'https://thewoobles.com/pages/start-round-piece-with-chain',
    //           desc: 'Magic ring too tricky? Try starting a round piece with a chain instead.',
    //         },
    //         {
    //           title: 'Slip knot',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-slip-knot-crochet_1296x.webp?v=1661382227',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-make-a-slip-knot-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-make-a-slip-knot',
    //           desc: 'This is the first step to begin some crochet techniques, such as a chain or slip stitch join.',
    //         },
    //         {
    //           title: 'Crochet in rows',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-crochet-in-rows_1296x.webp?v=1661382257',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-crochet-in-rows-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-crochet-in-rows',
    //           desc: 'How to create a slip knot, crochet a foundation chain, and the following rows.',
    //         },
    //         {
    //           title: 'Crochet an oval',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-crochet-an-oval-in-the-round_1_1296x.webp?v=1661382280',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-crochet-an-oval-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-crochet-an-oval',
    //           desc: 'Circles, rectangles, and now ovals, oh my!',
    //         },
    //         {
    //           title: 'Change colors',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-switch-colors-in-crochet_1296x.webp?v=1661382370',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-change-colors-in-the-round-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-change-colors-crocheting-in-the-round',
    //           desc: 'Learn how to change colors in the round and in rows.',
    //         },
    //         {
    //           title: 'Add another yarn ball',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-joining-yarn-in-crochet_80445ff0-5c30-474f-b4f8-ff658c81b4f0_1296x.webp?v=1661383282',
    //           url: left
    //             ? 'https://thewoobles.com/pages/add-yarn-ball-for-lefties'
    //             : 'https://thewoobles.com/pages/add-yarn-ball',
    //           desc: 'Ran out of yarn? Learn how to add another yarn ball.',
    //         },
    //         {
    //           title: 'Use safety eyes',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-insert-safety-eyes-in-crochet_1296x.webp?v=1661382523',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-use-safety-eyes-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-use-safety-eyes',
    //           desc: 'Also known as craft eyes, these are small pieces of round plastic that give life to your toys.',
    //         },
    //         {
    //           title: 'Stuff amigurumi',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-stuffing-amigurumi_1296x.webp?v=1661382564',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-stuff-amigurumi-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-stuff-amigurumi',
    //           desc: 'Two ways to stuff amigurumi to keep your piece looking smooth.',
    //         },
    //         {
    //           title: 'Close a ball',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-close-amigurumi_1296x.webp?v=1661382585',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-finish-a-closed-piece-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-finish-amigurumi',
    //           desc: 'How to fasten off and weave in the yarn tails of a self-contained piece of amigurumi, like a ball.',
    //         },
    //         {
    //           title: 'Hide yarn tails',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-hide-yarn-ends-in-crochet_1296x.webp?v=1661444980',
    //           url: left
    //             ? 'https://thewoobles.com/pages/hide-yarn-ends-for-lefties'
    //             : 'https://thewoobles.com/pages/hide-yarn-ends',
    //           desc: 'How to hide yarn tails in a 3D crochet piece.',
    //         },
    //         {
    //           title: 'Embroider eyes',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-embroidering-eyes-on-stuffed-animals_1296x.webp?v=1661445026',
    //           url: left
    //             ? 'https://thewoobles.com/pages/embroider-eyes-for-lefties'
    //             : 'https://thewoobles.com/pages/embroider-eyes',
    //           desc: 'Don&apos;t want to use plastic eyes? Embroider eyes instead.',
    //         },
    //         {
    //           title: 'Embroider blush',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-embroider-blush-amigurumi_1296x.webp?v=1661445102',
    //           url: left
    //             ? 'https://thewoobles.com/pages/embroider-blush-for-lefties'
    //             : 'https://thewoobles.com/pages/embroider-blush-on-crochet-amigurumi',
    //           desc: 'Embroider blush to give your amigurumi a little bit more charm.',
    //         },
    //         {
    //           title: 'Embroider a curve',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-embroider-curve-amigurumi_1296x.webp?v=1661445138',
    //           url: left
    //             ? 'https://thewoobles.com/pages/embroider-curve-for-lefties'
    //             : 'https://thewoobles.com/pages/embroider-curve',
    //           desc: 'Embroider a curve to give your amigurumi a smile or sleepy eyes.',
    //         },
    //         {
    //           title: 'Embroider a nose & mouth',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-embroider-a-mouth-on-a-stuffed-animal_1296x.webp?v=1661445173',
    //           url: left
    //             ? 'https://thewoobles.com/pages/embroider-nose-mouth-for-lefties'
    //             : 'https://thewoobles.com/pages/embroider-nose-mouth-on-amigurumi',
    //           desc: 'Embroider a simple nose and mouth onto amigurumi.',
    //         },
    //         {
    //           title: 'Fasten off',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-fasten-off-in-crochet_1296x.webp?v=1661382616',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-fasten-off-in-crochet-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-fasten-off-yarn-in-crochet',
    //           desc: 'When you&apos;re done crocheting a piece, fasten off to keep your stitches secure. Then either weave in the yarn tail or use it to sew this piece onto another.',
    //         },
    //         {
    //           title: 'Invisible fasten off for open edges',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-invisible-fasten-off-crochet_1296x.webp?v=1661445351',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-finish-an-open-piece-for-lefties'
    //             : 'https://thewoobles.com/pages/crochet-invisible-fasten-off',
    //           desc: 'How to fasten off, and prep a piece where the edge will be visible and even when attached to another piece.',
    //         },
    //         {
    //           title: 'Sew a flat piece onto a 3D piece',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-sew-two-crochet-pieces-together_1296x.webp?v=1661445442',
    //           url: left
    //             ? 'https://thewoobles.com/pages/sew-flat-piece-for-lefties'
    //             : 'https://thewoobles.com/pages/sew-flat-piece-onto-round-amigurumi-piece',
    //           desc: 'Learn how to sew a flat piece onto a 3D piece.',
    //         },
    //         {
    //           title: 'Sew amigurumi parts together',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-sew-amigurumi-pieces-together_1296x.webp?v=1661445464',
    //           url: left
    //             ? 'https://thewoobles.com/pages/how-to-join-amigurumi-pieces-for-lefties'
    //             : 'https://thewoobles.com/pages/how-to-join-amigurumi-pieces',
    //           desc: 'Now that you have all the pieces of your amigurumi, learn how to attach them together, also known as joining pieces',
    //         },
    //         {
    //           title: 'Weave in ends',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-weave-in-ends-crochet_1296x.webp?v=1661445520',
    //           url: left
    //             ? 'https://thewoobles.com/pages/weave-in-ends-for-lefties'
    //             : 'https://thewoobles.com/pages/weave-in-ends',
    //           desc: 'Weave in yarn tails to secure your piece.',
    //         },
    //         {
    //           title: 'Whip stitch',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-whip-stitch-in-crochet_1296x.webp?v=1661445553',
    //           url: left
    //             ? 'https://thewoobles.com/pages/whip-stitch-for-lefties'
    //             : 'https://thewoobles.com/pages/whip-stitch-in-crochet',
    //           desc: 'Sew two edges with the same number of stitches together.',
    //         },
    //         {
    //           title: 'Slip stitch join (sl st join)',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-join-with-a-slip-stitch-crochet_1296x.webp?v=1661445580',
    //           url: left
    //             ? 'https://thewoobles.com/pages/slip-stitch-join-for-lefties'
    //             : 'https://thewoobles.com/pages/slip-stitch-join-in-crochet',
    //           desc: 'Add yarn in a different direction with a slip stitch join.',
    //         },
    //         {
    //           title: 'Crochet through front or back loops only (flo, blo)',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/crochet-flo-blo_b198bed2-730d-4c1b-8fd6-e70d01fe194c_1296x.jpg?v=1661540452',
    //           url: left
    //             ? 'https://thewoobles.com/pages/front-back-loops-only-for-lefties'
    //             : 'https://thewoobles.com/pages/crochet-front-back-loops-only',
    //           desc: 'What it means to crochet through the front or back loops only.',
    //         },
    //         {
    //           title: 'Turn a chain into a ring',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-turning-a-crochet-chain-into-a-ring_fe0e4ae1-1d00-4fe3-9edf-427d146680c4_1296x.webp?v=1661445663',
    //           url: left
    //             ? 'https://thewoobles.com/pages/chain-to-ring-for-lefties'
    //             : 'https://thewoobles.com/pages/turn-crochet-chain-to-ring',
    //           desc: 'How to join the ends of a foundation chain to turn into a ring.',
    //         },
    //         {
    //           title: 'Find mistakes',
    //           img: 'https://cdn.shopify.com/s/files/1/0400/0904/0038/files/yt-how-to-fix-crochet-mistakes_1296x.webp?v=1661445694',
    //           url: left
    //             ? 'https://thewoobles.com/pages/find-mistakes-for-lefties'
    //             : 'https://thewoobles.com/pages/find-mistakes-in-crochet',
    //           desc: 'Mistakes are part of the crocheting process, and luckily they&apos;re easy to find and fix.',
    //         },
    //       ],
    //     },
    //   ],
    // },
    {
      display_name: 'Sugar & Snow the Two Peas in a Pod',
      content: `
        <iframe width="100%" height="216" src="https://www.youtube.com/embed/PTJI3VBGeXA" title="Make a pea-sized bow" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
        <iframe width="100%" height="216" src="https://www.youtube.com/embed/9pqM2UIZohw" title="Fold the peapod" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
        `,
      steps: [],
    },
    {
      display_name: 'Skipper and Boom the Otters',
      content: left
        ? '<iframe width="100%" height="216" src="https://www.youtube.com/embed/jlHgtKRjk4s" title="Left-handed // Crochet an otter arm" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>'
        : '<iframe width="100%" height="216" src="https://www.youtube.com/embed/9fmFBXXXVlU" title="Crochet an otter arm" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>',
      steps: [],
    },
    {
      display_name: 'Urkel Philibert the Stork',
      content: left
        ? '<iframe width="100%" height="216" src="https://www.youtube.com/embed/rdRFyTdbaKA" title="Left-handed // Finish the stork bag" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>'
        : '<iframe width="100%" height="216" src="https://www.youtube.com/embed/viPwW2oGD4A" title="Finish the stork bag" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>',
      steps: [],
    },
    {
      display_name: 'Pippa the Octopus',
      content: left
        ? '<iframe width="100%" height="216" src="https://www.youtube.com/embed/TDtlvzUCij4" title="Left-handed // Crochet 8 octopus legs together" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>'
        : '<iframe width="100%" height="216" src="https://www.youtube.com/embed/LLkn8J0P3hQ" title="Crochet 8 octopus legs together" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>',
      steps: [],
    },
    {
      display_name: 'Fred the Dino',
      content: left
        ? '<iframe width="100%" height="216" src="https://www.youtube.com/embed/sLGcyrp_AjY" title="Left-handed // Crochet a dinosaur arm" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>'
        : '<iframe width="100%" height="216" src="https://www.youtube.com/embed/W1CJqg-Gbb4" title="Crochet a dinosaur arm" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>',
      steps: [],
    },
    {
      display_name: 'Sam the Sloth',
      content:
        '<iframe width="100%" height="216" src="https://www.youtube.com/embed/SIwNoDIY408" title="Sew together sloth arms" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>',
      steps: [],
    },
    {
      display_name: 'Walter the Bear',
      content: `
          ${
            left
              ? '<iframe width="100%" height="216" src="https://www.youtube.com/embed/oYSuXwXgKHs" title="Left-handed // Crochet frosting drips" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>'
              : '<iframe width="100%" height="216" src="https://www.youtube.com/embed/kfXRQFCQftk" title="Crochet frosting drips" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>'
          }
          ${
            left
              ? '<iframe width="100%" height="216" src="https://www.youtube.com/embed/aWhxD2QczG0" title="Left-handed // Crochet round 37 of the cupcake liner" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>'
              : '<iframe width="100%" height="216" src="https://www.youtube.com/embed/HXr3QJ8Mags" title="Crochet round 37 of the cupcake liner" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>'
          }
          `,
      steps: [],
    },
  ].map(section => ({
    ...section,
    steps: section.steps.map(step => ({
      ...step,
      content: !Array.isArray(step.content)
        ? step.content
        : step.content
            .map(c => {
              return `
          <div style="margin-bottom: 40px;">
            <a href=${c.url}>
              <img src="${c.img}"/>
            </a>
            <h3 style="margin: 10px 0px;"><a href=${c.url}>${c.title}</a></h3>
            <p>${c.desc}
          </div>
          `;
            })
            .join(''),
    })),
  })),
});

const lite = [
  {
    id: 'recpWzru6964QhmLO',
    id_prod: 'recTxbylnMUtazZys',
    noHands: true,
    left: harrietSections,
    right: harrietSections,
  },
  {
    id: 'recr9qPoF33173wUb',
    id_prod: 'recVK2WfWGRqrl9HP',
    noHands: true,
    left: hippoSections,
    right: hippoSections,
  },
  {
    id: 'recCmgu4AZuB5yV74',
    id_prod: 'rec6XSBVRCi0pQyUI',
    noHands: true,
    left: foxSections,
    right: foxSections,
  },
  {
    id: 'recf8pnozB4EaWm8G',
    id_prod: 'recJJ1ufQeS3ueZVk',
    noHands: true,
    left: klausSections,
    right: klausSections,
  },
  {
    id: 'rec0YPWP6uPgCB5xl',
    id_prod: 'recuzr3Gn7DFWTIkZ',
    noHands: true,
    left: bearSections,
    right: bearSections,
  },
  {
    id: 'recq1WuLfi8Zf4hU1',
    id_prod: 'recx7nkOOSvowtE58',
    password_prompt: `
    To start wooblin', type in the last word on page 21 of your book.
    `,
    left: generalSections(true),
    right: generalSections(),
  },
  {
    id: 'rec68x7Uzegswe1UJ',
    id_prod: 'recAJ9eLQR4RQwEHn',
    left: {
      sections: [
        {
          display_name: 'Getting started',
          steps: [
            {
              display_name: `
              What's in your kit`,
              content: `
           <ul>
             <li>4mm crochet hook (if you paid for it)</li>
             <li>Stitch marker attached to green yarn</li>
             <li>Tapestry needle</li>
             <li>8mm safety eyes</li>
             <li>6mm safety eyes</li>
             <li>Stuffing</li>
             <li>Pink yarn</li>
             <li>Green yarn, with some stitches already made and a stitch marker attached</li>
           </ul>
           ${gettingStarted({ left: true })}
           `,
            },
          ],
        },
        basics({ left: true }),
        {
          display_name: 'The body',
          steps: [
            ...body({ left: true }),
            {
              display_name: 'Round 4-6',
              content: `
            <p>If you made it this far, take a moment to relish in your progress. You've learned 2 of the 4 stitches you need to finish this pattern!</p>
            <p>Let's turn up the heat. Now that you know how to read a pattern, try Rounds 4-6 by yourself.</p>
            <p class="ctr">Round 4-6: 18 sc   (18)</p>
            <p>That's 18 sc in Round 4, 18 sc in Round 5, and 18 sc in Round 6. Need a refresher on how to do the single crochet stitch? Watch the tutorial.</p>
            <h3>Right vs. wrong side</h3>
            <p>Your piece probably started curling. If you want what's called the "right" side of your piece to face out, flip it inside out:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/I95WSt7KYPE" title="How to identify the right side of crochet in the round" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            {
              display_name: 'Round 7',
              content: `
            <p>New stitch alert! Meet the decrease (dec) stitch:</p>
            <p class="ctr">Round 7: [sc, dec] x 6   (12)</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/3cceIZdQoD4" title="How to decrease invisibly (dec) in amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            eyes,
            {
              display_name: 'Finishing the pea',
              content: `
            <p>Here it is – the last round of the body!</p>
            <p class="ctr">Round 8: dec x 6   (6)</p>
            <p>You might be looking at your piece and wondering – now wait a minute. Why is there a giant gaping hole in the middle of my piece?</p>
            <p>Never fear, there's a tutorial here:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/TxssrWH56Fk" title="Left Handed // How to close an amigurumi ball" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            {
              display_name: 'The second pea',
              content: `
            <p>Brace yourself. It's time. Time to learn the magic loop. Pick up the green yarn and follow this tutorial to make the second pea from scratch:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/km5tyy1EK54" title="Easy way to crochet a magic loop" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            <p>Magic loop giving you trouble? Try this easier chain method instead. It'll probably leave you with a hole in the middle, but that's OK – you gotta do what you gotta do to make a circle.</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/K3NgWW8xs9g" title="How to start a round crochet piece with a chain (no magic loop!)" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            <p>Whether you made a circle using the magic loop or chain method, give yourself a pat on the back for finishing Round 1!</p>
            <p>Repeat rounds 2-8 to finish the second pea.</p>
            `,
            },
            {
              display_name: 'The baby pea',
              content: `
            <p>Let's turn these two peas into a hap-pea family by making a lil' baby pea:</p>
            <p><b>Round 1</b>:	5 sc in magic loop	(5)</p>
            <p><b>Round 2</b>:	inc x 5	(10)</p>
            <p><b>Round 3</b>:	[sc, inc] x 5	(15)</p>
            <p><b>Round 4</b>:	15 sc	(15)</p>
            <p>Insert the 6mm safety eyes between rounds 3 & 4, with 3 stitches between them. Remember that if this toy is for a baby or pet, embroider the eyes instead! Stuff your pea.</p>
            <p><b>Round 5</b>:	[sc, dec] x 5	(10)</p>
            <p><b>Round 6</b>:	dec x 5	(5)</p>
            <p>Fasten off, & close the remaining gap.</p>`,
            },
            {
              display_name: 'Embroidering blush',
              content: `
            <p>Now for some details! Add some little specks of blush under the peas' eyes:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/-DoIFLyDbMg" title="How to embroider blush on amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
          ],
        },
        {
          display_name: 'The pod',
          steps: [
            {
              display_name: 'The pod',
              content: `
              <p>Now that you've learned how to go round and round, it's time to learn how to crochet in straight lines. (Hats and scarves, here we come!)</p>
              <p>Crocheting in straight lines is called "crocheting in rows", which is why patterns talk about rows instead of rounds:

              <p><b>Row 1</b>:	ch 33	(33)</p>
              <p><b>Rows 2-15</b>:	sc in the 2nd stitch from the hook, and continue to sc until the end of the row. ch 1 and turn.</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/SlRsmXvMwwI" title="How to crochet in rows" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              <p>When you're done, fasten off and weave in the yarn tails. Heads up – even though the words are the same, the way you do them is different from how you did them for the peas:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/QyEiwzAXZAk" title="How to fasten off and weave in ends in crochet rows" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              <p>The last step is to sew this rectangle into a pea pod shape. It just involves a little origami magic and some green yarn:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/YHfry65sEAM" title="Fold the pea pod" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              ${goodBye('https://cdn.shopify.com/s/files/1/0400/0904/0038/products/3peas_out_600x600.jpg?v=1611161052')}
            `,
            },
          ],
        },
      ],
    },
    right: {
      sections: [
        {
          display_name: 'Getting started',
          steps: [
            {
              display_name: `
              What's in your kit`,
              content: `
           <ul>
             <li>4mm crochet hook (if you paid for it)</li>
             <li>Stitch marker attached to green yarn</li>
             <li>Tapestry needle</li>
             <li>8mm safety eyes</li>
             <li>6mm safety eyes</li>
             <li>Stuffing</li>
             <li>Pink yarn</li>
             <li>Green yarn, with some stitches already made and a stitch marker attached</li>
           </ul>
           ${gettingStarted({ left: false })}
           `,
            },
          ],
        },
        basics({ left: false }),
        {
          display_name: 'The body',
          steps: [
            ...body({ left: false }),
            {
              display_name: 'Round 4-6',
              content: `
            <p>If you made it this far, take a moment to relish in your progress. You've learned 2 of the 4 stitches you need to finish this pattern!</p>
            <p>Let's turn up the heat. Now that you know how to read a pattern, try Rounds 4-6 by yourself.</p>
            <p class="ctr">Round 4-6: 18 sc   (18)</p>
            <p>That's 18 sc in Round 4, 18 sc in Round 5, and 18 sc in Round 6. Need a refresher on how to do the single crochet stitch? Watch the tutorial.</p>
            <h3>Right vs. wrong side</h3>
            <p>Your piece probably started curling. If you want what's called the "right" side of your piece to face out, flip it inside out:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/I95WSt7KYPE" title="How to identify the right side of crochet in the round" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            {
              display_name: 'Round 7',
              content: `
            <p>New stitch alert! Meet the decrease (dec) stitch:</p>
            <p class="ctr">Round 7: [sc, dec] x 6   (12)</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/3cceIZdQoD4" title="How to decrease invisibly (dec) in amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            eyes,
            {
              display_name: 'Finishing the pea',
              content: `
            <p>Here it is – the last round of the body!</p>
            <p class="ctr">Round 8: dec x 6   (6)</p>
            <p>You might be looking at your piece and wondering – now wait a minute. Why is there a giant gaping hole in the middle of my piece?</p>
            <p>Never fear, there's a tutorial here:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/BMy0UJfQSU0" title="How to close an amigurumi ball" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            {
              display_name: 'The second pea',
              content: `
            <p>Brace yourself. It's time. Time to learn the magic loop. Pick up the green yarn and follow this tutorial to make the second pea from scratch:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/VPYymY9ni20" title="Easy way to crochet a magic loop" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            <p>Magic loop giving you trouble? Try this easier chain method instead. It'll probably leave you with a hole in the middle, but that's OK – you gotta do what you gotta do to make a circle.</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/qR4jhiXk4KM" title="How to start a round crochet piece with a chain (no magic loop!)" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            <p>Whether you made a circle using the magic loop or chain method, give yourself a pat on the back for finishing Round 1!</p>
            <p>Repeat rounds 2-8 to finish the second pea.</p>
            `,
            },
            {
              display_name: 'The baby pea',
              content: `
            <p>Let's turn these two peas into a hap-pea family by making a lil' baby pea:</p>
            <p><b>Round 1</b>:	5 sc in magic loop	(5)</p>
            <p><b>Round 2</b>:	inc x 5	(10)</p>
            <p><b>Round 3</b>:	[sc, inc] x 5	(15)</p>
            <p><b>Round 4</b>:	15 sc	(15)</p>
            <p>Insert the 6mm safety eyes between rounds 3 & 4, with 3 stitches between them. Remember that if this toy is for a baby or pet, embroider the eyes instead! Stuff your pea.</p>
            <p><b>Round 5</b>:	[sc, dec] x 5	(10)</p>
            <p><b>Round 6</b>:	dec x 5	(5)</p>
            <p>Fasten off, & close the remaining gap.</p>`,
            },
            {
              display_name: 'Embroidering blush',
              content: `
            <p>Now for some details! Add some little specks of blush under the peas' eyes:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/-DoIFLyDbMg" title="How to embroider blush on amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
          ],
        },
        {
          display_name: 'The pod',
          steps: [
            {
              display_name: 'The pod',
              content: `
              <p>Now that you've learned how to go round and round, it's time to learn how to crochet in straight lines. (Hats and scarves, here we come!)</p>
              <p>Crocheting in straight lines is called "crocheting in rows", which is why patterns talk about rows instead of rounds:

              <p><b>Row 1</b>:	ch 33	(33)</p>
              <p><b>Rows 2-15</b>:	sc in the 2nd stitch from the hook, and continue to sc until the end of the row. ch 1 and turn.</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/qUJyZgNChG8" title="How to crochet in rows" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              <p>When you're done, fasten off and weave in the yarn tails. Heads up – even though the words are the same, the way you do them is different from how you did them for the peas:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/67ivUFSVk-o" title="How to fasten off and weave in ends in crochet rows" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              <p>The last step is to sew this rectangle into a pea pod shape. It just involves a little origami magic and some green yarn:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/YHfry65sEAM" title="Fold the pea pod" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              ${goodBye('https://cdn.shopify.com/s/files/1/0400/0904/0038/products/3peas_out_600x600.jpg?v=1611161052')}
            `,
            },
          ],
        },
      ],
    },
  },
  {
    id: 'recQgttMTxFRjzdnZ',
    id_prod: 'reckR5ADaatgDRQaD',
    left: {
      sections: [
        {
          display_name: 'Getting started',
          steps: [
            {
              display_name: `
              What's in your kit`,
              content: `
            <ul>
              <li>4mm hook (if you paid for it)</li>
              <li>10mm safety eyes</li>
              <li>Tapestry needle</li>
              <li>Pink yarn (or orange yarn)</li>
              <li>Yellow yarn</li>
              <li>Stuffing</li>
              <li>Orange yarn (or blue yarn) with some stitches already made and a stitch marker attached</li>
              <li>Scissors</li>
            </ul>
            ${gettingStarted({ left: true })}
         `,
            },
          ],
        },
        basics({ left: true }),
        {
          display_name: 'The body',
          steps: [
            ...body({ left: true }),
            {
              display_name: 'Round 4',
              content: `
            <p>If you made it this far, take a moment to relish in your progress. You've learned 2 of the 3 stitches you need to finish this pattern!</p>
            <p>Let's turn up the heat. Now that you know how to read a pattern, try Round 4 by yourself.</p>
            <p class="ctr">Round 4: [inc, 2 sc] x 6   (24)</p>

            <h3>Need a hint?</h3>
            <p class="ctr">Round 4: [inc, 2 sc] x 6   (24)</p>
            <p>inc = 2 single crochet stitches in the same hole</p>
            <p>2 sc = 1 single crochet stitch in the first hole, and 1 single crochet stitch in the next</p>
            <p>So therefore, do an increase stitch in the first hole. A single crochet stitch in the next. Then a single crochet stitch in the one after that.</p>
            <p>Repeat that sequence of stitches 6 times. You'll have 24 V's once you've finished this round.</p>
            `,
            },
            {
              display_name: 'Round 5',
              content: `
            <p>Here's your first round of purely nice and easy single crochet stitches!</p>
            <p class="ctr">Round 5: 24 sc   (24)</p>

            <h3>Right vs. wrong side</h3>
            <p>Your piece probably started curling. If you want what's called the "right" side of your piece to face out, flip it inside out:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/fq0zRks4nB8" title="Left-handed // The right side vs. wrong side of crochet" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            {
              display_name: 'Rounds 6-10',
              content: `
            <p>If you've made it this far, give yourself a high five</p>
            <p>You've made it past the hard part – figuring out the motions and following a pattern. For the next few rounds, you'll keep on practicing the sc and inc stitches:</p>
            <p><b>Round 6:</b>	[5 sc, inc] x 4	(28)</p>
            <p><b>Rounds 7-9:*</b>	28 sc	(28)</p>
            <p><b>Round 10:</b>	[6 sc, inc] x 4	(32)</p>
            <p><i>*Make 28 sc in Round 7, 28 sc in Round 8, and 28 sc in Round 9</i></p>
            `,
            },
            {
              display_name: 'Rounds 11-13 & changing colors',
              content: `
            <p>We're going to turn this bird into a lovebird by changing colors in the shape of a heart ❤️</p>
            <p>Instead of calling a color by its name, patterns sometimes call them things like Color A and Color B. This way, you have the freedom to pick your color combos. For Dai & Suki, the colors in your kit are:</p>
            <table>
              <thead>
                <tr>
                  <th>Color A</th>
                  <th>Color B</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Orange</td>
                  <td>Pink</td>
                </tr>
                <tr>
                  <td>Blue</td>
                  <td>Orange</td>
                </tr>
              </tbody>
            </table>
            <p>Here's how to change colors:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/rD8RJvqgcqE" title="Left-handed // Alternate colors while crocheting in the round" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            <p><b>Round 11:</b> 13 sc, (switch to color B) 2 sc, (switch to color A) 2 sc, (switch to color B) 2 sc, (switch to color A) 13 sc  (32)</p>
            <p><b>Round 12:</b> 12 sc, (switch to color B) 9 sc, (switch to color A) 11 sc  (32)</p>
            <p><b>Round 13:</b> 13 sc, (switch to color B) 8 sc, (switch to color A) 11 sc (32)</p>
            `,
            },
            {
              display_name: 'Rounds 14-17',
              content: `
            <p>New stitch alert! Meet the decrease (dec) stitch:</p>
            <p><b>Round 14:</b> [2 sc, dec] x 3, 2 sc, (switch to color B) dec, 2 sc, dec, (switch to color A) [2 sc, dec] x 3 (24)</p>
            <p><b>Round 15:</b> 13 sc, (switch to color B) sc, (switch to color A) 10 sc  (24)</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/enqg7zT3SVQ" title="Left-handed // How to decrease invisibly (dec) in amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            <p>Then cut Color B close to the last stitch you just made.</p>
            `,
            },
            eyes,
            {
              display_name: 'Finishing the body',
              content: `
              <p>Here it is – the last few rounds of the body!</p>

              <p><b>Round 16:</b>	[sc, dec] x 8	(16)</p>
              <p><b>Round 17:</b>	16 sc	(16)</p>
              <p><b>Round 18:</b>	dec x 8	(8)</p>
              <p>You might be looking at your piece and wondering – now wait a minute. Why is there a giant gaping hole in the middle of my piece?</p>

              <p>Never fear, there's a tutorial here:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/TxssrWH56Fk" title="Left-handed // How to close an amigurumi ball" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              `,
            },
          ],
        },
        {
          display_name: 'The finishing touches',
          steps: [
            {
              display_name: 'The wings & the magic loop',
              content: `
              <p>Brace yourself. It's time. Time to learn the magic loop. Pick up your main yarn color (either orange or blue) and watch this tutorial:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/E6yESAgMjwg" title="Left-handed // Magic loop with 4 single crochet (4 sc) stitches" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>

              <p>Magic loop giving you trouble? Try this easier chain method instead. It'll probably leave you with a hole in the middle, but that's OK – you gotta do what you gotta do to make a circle.</p>

              <p>Whether you made a circle using the magic loop or chain method, give yourself a pat on the back for finishing Round 1. Now make the rest of the wing:</p>

              <p><b>Round 1:</b>	4 sc in magic loop	(4)</p>
              <p><b>Round 2:</b>	inc x 4	(8)</p>
              <p><b>Round 3:</b>	8 sc	(8)</p>

              <p><b>Tip: </b>If you're having trouble seeing the stitches in this teeny tiny magic loop, use the 3 stitch markers that came in your kit and mark the stitches as you make them.</p>
              <p>Before finishing the piece, you'll do a few more stitches to seal the wings off:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/g2ZQLNClde0" title="Left-handed // Single crochet opposite sides of a piece together" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              <p>Once they're sealed off, they're ready to sew on between rounds 11 and 12 of the body, 3 stitches away from each eye: </p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/Gt_SUKHtuGE" title="How to sew amigurumi parts together" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              `,
            },
            {
              display_name: 'The tail',
              content: `
              <p>The last piece of the lovebird body is the tail. Grab your main yarn color and make another magic loop with 4 single crochet stitches in it.</p>
              <p><b>Round 1:</b>	4 sc in magic loop	(4)</p>
              <p><b>Round 2:</b>	4 sc	(4)</p>
              <p><b>Round 3:</b>	inc x 4	(8)</p>
              <p>Invisible fasten off leaving a long tail:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/grjYaiBJMfs" title="Left-handed // How to crochet an invisible fasten off" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              <p>Then use a needle and yarn tail to attach the tail to round 14.</p>
              `,
            },
            {
              display_name: 'The beak',
              content: `
              <p>And now for the final finishing touch! Embroider a beak with yellow yarn, using the same technique you'd use to embroider eyes:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/v4IqcQurAIA" title="Embroider eyes on crochet amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              ${goodBye(
                'https://cdn.shopify.com/s/files/1/0400/0904/0038/products/lovebirds_side_600x600.jpg?v=1642999220',
              )}
              `,
            },
          ],
        },
      ],
    },
    right: {
      sections: [
        {
          display_name: 'Getting started',
          steps: [
            {
              display_name: `
              What's in your kit`,
              content: `
            <ul>
              <li>4mm hook (if you paid for it)</li>
              <li>10mm safety eyes</li>
              <li>Tapestry needle</li>
              <li>Pink yarn (or orange yarn)</li>
              <li>Yellow yarn</li>
              <li>Stuffing</li>
              <li>Orange yarn (or blue yarn) with some stitches already made and a stitch marker attached</li>
              <li>Scissors</li>
            </ul>
            ${gettingStarted({ left: false })}
         `,
            },
          ],
        },
        basics({ left: false }),
        {
          display_name: 'The body',
          steps: [
            ...body({ left: false }),
            {
              display_name: 'Round 4',
              content: `
            <p>If you made it this far, take a moment to relish in your progress. You've learned 2 of the 3 stitches you need to finish this pattern!</p>
            <p>Let's turn up the heat. Now that you know how to read a pattern, try Round 4 by yourself.</p>
            <p class="ctr">Round 4: [inc, 2 sc] x 6   (24)</p>

            <h3>Need a hint?</h3>
            <p class="ctr">Round 4: [inc, 2 sc] x 6   (24)</p>
            <p>inc = 2 single crochet stitches in the same hole</p>
            <p>2 sc = 1 single crochet stitch in the first hole, and 1 single crochet stitch in the next</p>
            <p>So therefore, do an increase stitch in the first hole. A single crochet stitch in the next. Then a single crochet stitch in the one after that.</p>
            <p>Repeat that sequence of stitches 6 times. You'll have 24 V's once you've finished this round.</p>
            `,
            },
            {
              display_name: 'Round 5',
              content: `
            <p>Here's your first round of purely nice and easy single crochet stitches!</p>
            <p class="ctr">Round 5: 24 sc   (24)</p>

            <h3>Right vs. wrong side</h3>
            <p>Your piece probably started curling. If you want what's called the "right" side of your piece to face out, flip it inside out:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/dT1tnt3-hw8" title="The right side vs. wrong side of crochet" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            {
              display_name: 'Rounds 6-10',
              content: `
            <p>If you've made it this far, give yourself a high five</p>
            <p>You've made it past the hard part – figuring out the motions and following a pattern. For the next few rounds, you'll keep on practicing the sc and inc stitches:</p>
            <p><b>Round 6:</b>	[5 sc, inc] x 4	(28)</p>
            <p><b>Rounds 7-9:*</b>	28 sc	(28)</p>
            <p><b>Round 10:</b>	[6 sc, inc] x 4	(32)</p>
            <p><i>*Make 28 sc in Round 7, 28 sc in Round 8, and 28 sc in Round 9</i></p>
            `,
            },
            {
              display_name: 'Rounds 11-13 & changing colors',
              content: `
            <p>We're going to turn this bird into a lovebird by changing colors in the shape of a heart ❤️</p>
            <p>Instead of calling a color by its name, patterns sometimes call them things like Color A and Color B. This way, you have the freedom to pick your color combos. For Dai & Suki, the colors in your kit are:</p>
            <table>
              <thead>
                <tr>
                  <th>Color A</th>
                  <th>Color B</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Orange</td>
                  <td>Pink</td>
                </tr>
                <tr>
                  <td>Blue</td>
                  <td>Orange</td>
                </tr>
              </tbody>
            </table>
            <p>Here's how to change colors:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/LMS6K7HRP2s" title="Alternate colors while crocheting in the round" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            <p><b>Round 11:</b> 13 sc, (switch to color B) 2 sc, (switch to color A) 2 sc, (switch to color B) 2 sc, (switch to color A) 13 sc  (32)</p>
            <p><b>Round 12:</b> 12 sc, (switch to color B) 9 sc, (switch to color A) 11 sc  (32)</p>
            <p><b>Round 13:</b> 13 sc, (switch to color B) 8 sc, (switch to color A) 11 sc (32)</p>
            `,
            },
            {
              display_name: 'Rounds 14-17',
              content: `
            <p>New stitch alert! Meet the decrease (dec) stitch:</p>
            <p><b>Round 14:</b> [2 sc, dec] x 3, 2 sc, (switch to color B) dec, 2 sc, dec, (switch to color A) [2 sc, dec] x 3 (24)</p>
            <p><b>Round 15:</b> 13 sc, (switch to color B) sc, (switch to color A) 10 sc  (24)</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/3cceIZdQoD4" title="How to decrease invisibly (dec) in amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            <p>Then cut Color B close to the last stitch you just made.</p>
            `,
            },
            eyes,
            {
              display_name: 'Finishing the body',
              content: `
              <p>Here it is – the last few rounds of the body!</p>

              <p><b>Round 16:</b>	[sc, dec] x 8	(16)</p>
              <p><b>Round 17:</b>	16 sc	(16)</p>
              <p><b>Round 18:</b>	dec x 8	(8)</p>
              <p>You might be looking at your piece and wondering – now wait a minute. Why is there a giant gaping hole in the middle of my piece?</p>

              <p>Never fear, there's a tutorial here:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/BMy0UJfQSU0" title="How to close an amigurumi ball" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              `,
            },
          ],
        },
        {
          display_name: 'The finishing touches',
          steps: [
            {
              display_name: 'The wings & the magic loop',
              content: `
              <p>Brace yourself. It's time. Time to learn the magic loop. Pick up your main yarn color (either orange or blue) and watch this tutorial:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/EHrSEzcDAQg" title="Magic loop with 4 single crochet (4 sc) stitches" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>

              <p>Magic loop giving you trouble? Try this easier chain method instead. It'll probably leave you with a hole in the middle, but that's OK – you gotta do what you gotta do to make a circle.</p>

              <p>Whether you made a circle using the magic loop or chain method, give yourself a pat on the back for finishing Round 1. Now make the rest of the wing:</p>

              <p><b>Round 1:</b>	4 sc in magic loop	(4)</p>
              <p><b>Round 2:</b>	inc x 4	(8)</p>
              <p><b>Round 3:</b>	8 sc	(8)</p>

              <p><b>Tip: </b>If you're having trouble seeing the stitches in this teeny tiny magic loop, use the 3 stitch markers that came in your kit and mark the stitches as you make them.</p>
              <p>Before finishing the piece, you'll do a few more stitches to seal the wings off:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/KrnR1uqoSFQ" title="Single crochet opposite sides of a piece together" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              <p>Once they're sealed off, they're ready to sew on between rounds 11 and 12 of the body, 3 stitches away from each eye: </p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/Gt_SUKHtuGE" title="How to sew amigurumi parts together" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              `,
            },
            {
              display_name: 'The tail',
              content: `
              <p>The last piece of the lovebird body is the tail. Grab your main yarn color and make another magic loop with 4 single crochet stitches in it.</p>
              <p><b>Round 1:</b>	4 sc in magic loop	(4)</p>
              <p><b>Round 2:</b>	4 sc	(4)</p>
              <p><b>Round 3:</b>	inc x 4	(8)</p>
              <p>Invisible fasten off leaving a long tail:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/QaZWfxDnMWs" title="How to crochet an invisible fasten off for open edges" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              <p>Then use a needle and yarn tail to attach the tail to round 14.</p>
              `,
            },
            {
              display_name: 'The beak',
              content: `
              <p>And now for the final finishing touch! Embroider a beak with yellow yarn, using the same technique you'd use to embroider eyes:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/v4IqcQurAIA" title="Embroider eyes on crochet amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              ${goodBye(
                'https://cdn.shopify.com/s/files/1/0400/0904/0038/products/lovebirds_side_600x600.jpg?v=1642999220',
              )}
              `,
            },
          ],
        },
      ],
    },
  },
  {
    id: 'recnvBhLRhcod6urR',
    id_prod: 'recR6doC8U0Nxo7ev',
    left: {
      sections: [
        {
          display_name: 'Getting started',
          steps: [
            {
              display_name: `
              What's in your kit`,
              content: `
           <ul>
             <li>4mm crochet hook (if you paid for it)</li>
             <li>Stitch marker attached to green yarn</li>
             <li>Tapestry needle</li>
             <li>10mm safety eyes</li>
             <li>Stuffing</li>
             <li>White yarn</li>
             <li>Pink yarn</li>
             <li>Black yarn</li>
             <li>Green yarn, with some stitches already made and a stitch marker attached</li>
           </ul>
           ${gettingStarted({ left: true })}
           `,
            },
          ],
        },
        basics({ left: true }),
        {
          display_name: 'The body',
          steps: [
            ...body({ left: true }),
            {
              display_name: 'Round 4-6',
              content: `
            <p>If you made it this far, take a moment to relish in your progress. You've learned 2 of the 4 stitches you need to finish this pattern!</p>
            <p>Let's turn up the heat. Now that you know how to read a pattern, try Rounds 4-6 by yourself.</p>
            <p class="ctr">Round 4-6: 18 sc   (18)</p>
            <p>That's 18 sc in Round 4, 18 sc in Round 5, and 18 sc in Round 6. Need a refresher on how to do the single crochet stitch? Watch the tutorial.</p>
            <h3>Right vs. wrong side</h3>
            <p>Your piece probably started curling. If you want what's called the "right" side of your piece to face out, flip it inside out:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/fq0zRks4nB8" title="Left-handed // The right side vs. wrong side of crochet" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            {
              display_name: 'Round 7',
              content: `
            <p>New stitch alert! Meet the decrease (dec) stitch:</p>
            <p class="ctr">Round 7: [sc, dec] x 6   (12)</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/enqg7zT3SVQ" title="Left-handed // How to decrease invisibly (dec) in amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            eyes,
            {
              display_name: 'Finishing the pea',
              content: `
            <p>Here it is – the last round of the body!</p>
            <p class="ctr">Round 8: dec x 6   (6)</p>
            <p>You might be looking at your piece and wondering – now wait a minute. Why is there a giant gaping hole in the middle of my piece?</p>
            <p>Never fear, there's a tutorial here:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/TxssrWH56Fk" title="Left Handed // How to close an amigurumi ball" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            {
              display_name: 'The second pea',
              content: `
            <p>Brace yourself. It's time. Time to learn the magic loop. Pick up the green yarn and follow this tutorial to make the second pea from scratch:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/IM7_AdKahjU" title="Left-handed // How to crochet a magic loop" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            <p>Magic loop giving you trouble? Try this easier chain method instead. It'll probably leave you with a hole in the middle, but that's OK – you gotta do what you gotta do to make a circle.</p>
            <p>Whether you made a circle using the magic loop or chain method, give yourself a pat on the back for finishing Round 1!</p>
            <p>Repeat rounds 2-8 to finish the second pea.</p>
            `,
            },
            {
              display_name: 'Embroidering blush',
              content: `
            <p>Now for some details! Add some little specks of blush under the peas' eyes:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/-DoIFLyDbMg" title="How to embroider blush on amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            {
              display_name: 'The bows',
              content: `
            <p>Complete Sugar and Snow's look with a bow. A hair bow, a bow tie, or wherever else you feel like putting it – there's bow limits!</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/svSHHo1mpSo" title="Make the bows" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
          ],
        },
        {
          display_name: 'The pod',
          steps: [
            {
              display_name: 'The pod',
              content: `
              <p>Now that you've learned how to go round and round, it's time to learn how to crochet in straight lines. (Hats and scarves, here we come!)</p>
              <p>Crocheting in straight lines is called "crocheting in rows", which is why patterns talk about rows instead of rounds:

              <p><b>Row 1</b>:  ch 25	(25)</p>
              <p><b>Rows 2-13</b>:  sc in the 2nd stitch from the hook, and continue to sc until the end of the row. ch 1 and turn. (24)</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/SlRsmXvMwwI" title="Left-handed // How to crochet in rows" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              <p>When you're done, fasten off and weave in the yarn tails. Heads up – even though the words are the same, the way you do them is different from how you did them for the peas:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/QyEiwzAXZAk" title="Left-handed // How to fasten off and weave in ends in crochet rows" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              <p>The last step is to sew this rectangle into a pea pod shape. It just involves a little origami magic and some green yarn:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/YHfry65sEAM" title="Fold the pea pod" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              ${goodBye('https://cdn.shopify.com/s/files/1/0400/0904/0038/files/2peas_600x600.jpg?v=1604185972')}
            `,
            },
          ],
        },
      ],
    },
    right: {
      sections: [
        {
          display_name: 'Getting started',
          steps: [
            {
              display_name: `
              What's in your kit`,
              content: `
           <ul>
             <li>4mm crochet hook (if you paid for it)</li>
             <li>Stitch marker attached to green yarn</li>
             <li>Tapestry needle</li>
             <li>10mm safety eyes</li>
             <li>Stuffing</li>
             <li>White yarn</li>
             <li>Pink yarn</li>
             <li>Black yarn</li>
             <li>Green yarn, with some stitches already made and a stitch marker attached</li>
           </ul>
           ${gettingStarted({ left: false })}
           `,
            },
          ],
        },
        basics({ left: false }),
        {
          display_name: 'The body',
          steps: [
            ...body({ left: false }),
            {
              display_name: 'Round 4-6',
              content: `
            <p>If you made it this far, take a moment to relish in your progress. You've learned 2 of the 4 stitches you need to finish this pattern!</p>
            <p>Let's turn up the heat. Now that you know how to read a pattern, try Rounds 4-6 by yourself.</p>
            <p class="ctr">Round 4-6: 18 sc   (18)</p>
            <p>That's 18 sc in Round 4, 18 sc in Round 5, and 18 sc in Round 6. Need a refresher on how to do the single crochet stitch? Watch the tutorial.</p>
            <h3>Right vs. wrong side</h3>
            <p>Your piece probably started curling. If you want what's called the "right" side of your piece to face out, flip it inside out:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/dT1tnt3-hw8" title="The right side vs. wrong side of crochet" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            {
              display_name: 'Round 7',
              content: `
            <p>New stitch alert! Meet the decrease (dec) stitch:</p>
            <p class="ctr">Round 7: [sc, dec] x 6   (12)</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/3cceIZdQoD4" title="How to decrease invisibly (dec) in amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            eyes,
            {
              display_name: 'Finishing the pea',
              content: `
            <p>Here it is – the last round of the body!</p>
            <p class="ctr">Round 8: dec x 6   (6)</p>
            <p>You might be looking at your piece and wondering – now wait a minute. Why is there a giant gaping hole in the middle of my piece?</p>
            <p>Never fear, there's a tutorial here:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/BMy0UJfQSU0" title="How to close an amigurumi ball" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            {
              display_name: 'The second pea',
              content: `
            <p>Brace yourself. It's time. Time to learn the magic loop. Pick up the green yarn and follow this tutorial to make the second pea from scratch:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/F-s-NSEPfq4" title="Easy way to crochet a magic loop" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            <p>Magic loop giving you trouble? Try this easier chain method instead. It'll probably leave you with a hole in the middle, but that's OK – you gotta do what you gotta do to make a circle.</p>
            <p>Whether you made a circle using the magic loop or chain method, give yourself a pat on the back for finishing Round 1!</p>
            <p>Repeat rounds 2-8 to finish the second pea.</p>
            `,
            },
            {
              display_name: 'Embroidering blush',
              content: `
            <p>Now for some details! Add some little specks of blush under the peas' eyes:</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/-DoIFLyDbMg" title="How to embroider blush on amigurumi" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
            {
              display_name: 'The bows',
              content: `
            <p>Complete Sugar and Snow's look with a bow. A hair bow, a bow tie, or wherever else you feel like putting it – there's bow limits!</p>
            <iframe width="100%" height="216" src="https://www.youtube.com/embed/svSHHo1mpSo" title="Make the bows" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            `,
            },
          ],
        },
        {
          display_name: 'The pod',
          steps: [
            {
              display_name: 'The pod',
              content: `
              <p>Now that you've learned how to go round and round, it's time to learn how to crochet in straight lines. (Hats and scarves, here we come!)</p>
              <p>Crocheting in straight lines is called "crocheting in rows", which is why patterns talk about rows instead of rounds:

              <p><b>Row 1</b>:	ch 33	(33)</p>
              <p><b>Rows 2-15</b>:	sc in the 2nd stitch from the hook, and continue to sc until the end of the row. ch 1 and turn.</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/qUJyZgNChG8" title="How to crochet in rows" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              <p>When you're done, fasten off and weave in the yarn tails. Heads up – even though the words are the same, the way you do them is different from how you did them for the peas:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/67ivUFSVk-o" title="How to fasten off and weave in ends in crochet rows" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              <p>The last step is to sew this rectangle into a pea pod shape. It just involves a little origami magic and some green yarn:</p>
              <iframe width="100%" height="216" src="https://www.youtube.com/embed/YHfry65sEAM" title="Fold the pea pod" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
              ${goodBye('https://cdn.shopify.com/s/files/1/0400/0904/0038/files/2peas_600x600.jpg?v=1604185972')}
            `,
            },
          ],
        },
      ],
    },
  },
];

export default lite;
